'use client';
/** @jsxImportSource @emotion/react */

import React, { type ReactNode, useCallback, useRef } from 'react';
import { type VirtuosoHandle } from 'react-virtuoso';

import { MenuItem } from '../../../../menu-item/MenuItem';
import { VirtualizedList } from '../../virtualized-list/VirtualizedList';
import { useDropdownContent } from '../dropdown-context';

type DropdownVirtualizedListProps<T> = {
  /**
   * Function to render each item in the virtualized list
   */
  children: (item: T) => ReactNode;
  /**
   * Height of the dropdown menu
   */
  height?: number;
};

export const DropdownVirtualizedList = <T,>({
  children,
  height = 280,
}: DropdownVirtualizedListProps<T>) => {
  const {
    filteredItems,
    focusIndex,
    isMobile,
    MenuListProps,
    onMenuItemClick,
    setFocusIndex,
  } = useDropdownContent<T>();
  const ref = useRef<VirtuosoHandle>(null);
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      if (!ref.current) {
        return;
      }

      const indexToSet = (() => {
        if (e.key === 'ArrowDown') {
          if (filteredItems.length - 1 === index) {
            return 0;
          }

          return (index + 1) % filteredItems.length;
        }
        if (e.key === 'ArrowUp') {
          if (index === 0) {
            return filteredItems.length - 1;
          }
          return (index - 1 + filteredItems.length) % filteredItems.length;
        }
        return index;
      })();

      ref.current.scrollIntoView({
        behavior: 'auto',
        done: () => {
          setFocusIndex?.(indexToSet);
        },
        index: indexToSet,
      });
    },
    [filteredItems.length, setFocusIndex]
  );

  return (
    <VirtualizedList
      components={{
        Item: ({ children: nodes, ...props }) => {
          return (
            <MenuItem
              {...props}
              style={{ whiteSpace: 'normal', ...props.style }}
              onClick={() => onMenuItemClick?.(props.item)}
              onKeyDown={(event) => {
                onKeyDown(event, props['data-index']);
              }}
              {...(MenuListProps?.component === 'div' && { component: 'div' })}
              {...(props['data-index'] === 0 && { tabIndex: 0 })}
              autoFocus={props['data-index'] === focusIndex}
            >
              {nodes}
            </MenuItem>
          );
        },
      }}
      items={filteredItems}
      overscan={{ main: 36, reverse: 36 }}
      virtuosoRef={ref}
      {...(!isMobile && { style: { height } })}
    >
      {(item) => children(item)}
    </VirtualizedList>
  );
};
